import { AIForMeTool } from "lib/models/aiforme-models";
import { useCallback, useMemo, useState } from "react";

import { MAX_COMPARE_TOOLS, MIN_COMPARE_TOOLS } from "./constants";

export interface UseCompareToolsState {
  /** Array of selected tools for comparison */
  selectedTools: AIForMeTool[];
  /** Whether the comparison flyout is open */
  isFlyoutOpen: boolean;
  /** Whether more tools can be added (less than max) */
  canAddMore: boolean;
  /** Whether comparison can be started (at least min tools selected) */
  canCompare: boolean;
  /** Number of currently selected tools */
  selectedCount: number;
  /** Set of selected tool taxonomy IDs for O(1) lookups */
  selectedToolIds: Set<string>;
}

export interface UseCompareToolsActions {
  /** Add a tool to the comparison selection */
  addTool: (tool: AIForMeTool) => void;
  /** Remove a tool from the comparison selection by taxonomy ID */
  removeTool: (taxonomyId: string) => void;
  /** Clear all selected tools */
  clearAll: () => void;
  /** Check if a tool is currently selected */
  isToolSelected: (taxonomyId: string) => boolean;
  /** Open the comparison flyout */
  openFlyout: () => void;
  /** Close the comparison flyout */
  closeFlyout: () => void;
}

export type UseCompareToolsReturn = UseCompareToolsState & UseCompareToolsActions;

/**
 * Hook for managing tool comparison state
 * Handles selection, and flyout visibility
 */
export const useCompareTools = (): UseCompareToolsReturn => {
  const [selectedTools, setSelectedTools] = useState<AIForMeTool[]>([]);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

  // Computed values
  const selectedCount = selectedTools.length;
  const canAddMore = selectedCount < MAX_COMPARE_TOOLS;
  const canCompare = selectedCount >= MIN_COMPARE_TOOLS;

  // Stable Set for O(1) lookups — only recomputed when selectedTools changes
  const selectedToolIds = useMemo(() => new Set(selectedTools.map((t) => t.taxonomyId)), [selectedTools]);

  /**
   * Add a tool to the comparison selection
   */
  const addTool = useCallback((tool: AIForMeTool) => {
    setSelectedTools((prev) => {
      // Check if already selected
      if (prev.some((t) => t.taxonomyId === tool.taxonomyId)) {
        return prev;
      }
      // Check if at max
      if (prev.length >= MAX_COMPARE_TOOLS) {
        return prev;
      }
      const updated = [...prev, tool];
      return updated;
    });
  }, []);

  /**
   * Remove a tool from the comparison selection
   */
  const removeTool = useCallback(
    (taxonomyId: string) => {
      setSelectedTools((prev) => {
        const updated = prev.filter((t) => t.taxonomyId !== taxonomyId);

        // Auto-close flyout if no tools remain
        if (updated.length === 0 && isFlyoutOpen) {
          setIsFlyoutOpen(false);
        }

        return updated;
      });
    },
    [isFlyoutOpen]
  );

  /**
   * Clear all selected tools
   */
  const clearAll = useCallback(() => {
    setSelectedTools([]);
    setIsFlyoutOpen(false);
  }, []);

  /**
   * Check if a tool is currently selected
   */
  const isToolSelected = useCallback(
    (taxonomyId: string): boolean => {
      return selectedToolIds.has(taxonomyId);
    },
    [selectedToolIds]
  );

  /**
   * Open the comparison flyout
   */
  const openFlyout = useCallback(() => {
    if (canCompare) {
      setIsFlyoutOpen(true);
    }
  }, [canCompare]);

  /**
   * Close the comparison flyout
   */
  const closeFlyout = useCallback(() => {
    setIsFlyoutOpen(false);
  }, []);

  return {
    // State
    selectedTools,
    isFlyoutOpen,
    canAddMore,
    canCompare,
    selectedCount,
    selectedToolIds,
    // Actions
    addTool,
    removeTool,
    clearAll,
    isToolSelected,
    openFlyout,
    closeFlyout,
  };
};
